import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { Building2 } from "lucide-react";

import { Card } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { PageHeader } from "~/components/ui/page-title";
import { myOrganizationsQueryOptions } from "~/lib/functions/cco-representatives";

import { OrganizationMark } from "./-organization-mark";

/**
 * Where "Open My organization" in an email lands. One organization opens
 * directly; with several, the person picks one.
 */
export const Route = createFileRoute("/_user/my-organization/")({
  staticData: { title: "My organization" },
  loader: async ({ context }) => {
    const organizations = await context.queryClient.fetchQuery(
      myOrganizationsQueryOptions(),
    );
    if (organizations.length === 1) {
      throw redirect({
        to: "/my-organization/$slug",
        params: { slug: organizations[0].slug },
      });
    }
    return { organizations };
  },
  component: MyOrganizationsPage,
});

function MyOrganizationsPage(): React.JSX.Element {
  const { organizations } = Route.useLoaderData();

  if (organizations.length === 0) {
    return (
      <EmptyState
        icon={Building2}
        title="You don't represent an organization yet"
        description="Ask from your organization's page on Dream Coach Match, or reply to your invitation email if you have one."
      />
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title="My organization"
        description="You represent more than one organization. Choose the one to work on."
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {organizations.map((organization) => (
          <Link
            key={organization.ccoId}
            to="/my-organization/$slug"
            params={{ slug: organization.slug }}
          >
            <Card variant="interactive" className="flex-row items-center">
              <OrganizationMark
                name={organization.name}
                logoUrl={organization.logoUrl}
              />
              <span className="text-base font-medium text-gray-900">
                {organization.name}
              </span>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
